/**
 * Chat view - declarative React component.
 * 
 * Elm-ish architecture: This component is THIN.
 * - No business logic
 * - No state mutations
 * - No API calls
 * - Only renders based on model state
 * - Delegates all actions to callbacks from the program
 * 
 * Scope: server_readable channels only.
 * client_sealed (E2EE) is intentionally not exposed.
 */
import { Link } from 'react-router-dom';
import { RoutePaths } from '@/routes';
import { ChatHeader } from './components/ChatHeader';
import type { ChatModel } from './model';
import { canSendMessage } from './model';

/**
 * Props for the view component.
 */
export interface ChatViewProps {
  /** Current model state */
  model: ChatModel;
  /** Start the chat program */
  onStart: () => void;
  /** Open a channel */
  onOpenChannel: (channelId: string) => void;
  /** Go back to channel list */
  onBackToList: () => void;
  /** Show create channel form */
  onShowCreateForm: () => void;
  /** Hide create channel form */
  onHideCreateForm: () => void;
  /** Create a new channel */
  onCreateChannel: () => void;
  /** Send a message */
  onSendMessage: () => void;
  /** Message text changed */
  onNewMessageChanged: (text: string) => void;
  /** Retry after error */
  onRetry: () => void;
}

/**
 * Format a date for display.
 */
function formatDate(date: Date): string {
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Loading state component.
 */
function LoadingState() {
  return (
    <div className="chat-loading" data-testid="chat-loading">
      <p>Loading...</p>
    </div>
  );
}

/**
 * Error state component.
 */
function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="chat-error" data-testid="chat-error">
      <div className="error-icon">⚠</div>
      <h2 className="error-title">Something went wrong</h2>
      <p className="error-message">{message}</p>
      <div className="error-actions">
        <button onClick={onRetry} className="retry-button">
          Try Again
        </button>
      </div>
    </div>
  );
}

/**
 * Empty state for channel list.
 */
function EmptyChannelList({ onCreate }: { onCreate: () => void }) {
  return (
    <div className="chat-empty" data-testid="chat-empty">
      <div className="empty-icon">💬</div>
      <h2 className="empty-title">No channels yet</h2>
      <p className="empty-message">
        Create your first chat channel to start messaging.
      </p>
      <div className="empty-actions">
        <button onClick={onCreate} className="create-button">
          Create Channel
        </button>
      </div>
    </div>
  );
}

/**
 * Channel list item.
 */
function ChannelListItem({
  channel,
  onClick,
}: {
  channel: { id: string; createdAt: Date };
  onClick: () => void;
}) {
  return (
    <button
      className="channel-item"
      onClick={onClick}
      data-testid={`channel-${channel.id}`}
    >
      <div className="channel-icon">💬</div>
      <div className="channel-info">
        <span className="channel-name">Channel</span>
        <span className="channel-date">{formatDate(channel.createdAt)}</span>
      </div>
      <div className="channel-arrow">›</div>
    </button>
  );
}

/**
 * Channel list component.
 */
function ChannelList({
  channels,
  onOpenChannel,
  onCreate,
}: {
  channels: readonly { id: string; createdAt: Date }[];
  onOpenChannel: (channelId: string) => void;
  onCreate: () => void;
}) {
  if (channels.length === 0) {
    return <EmptyChannelList onCreate={onCreate} />;
  }

  return (
    <div className="channel-list" data-testid="channel-list">
      {channels.map(channel => (
        <ChannelListItem
          key={channel.id}
          channel={channel}
          onClick={() => onOpenChannel(channel.id)}
        />
      ))}
    </div>
  );
}

/**
 * Create channel confirmation dialog.
 */
function CreateChannelConfirm({
  onConfirm,
  onCancel,
  isCreating,
}: {
  onConfirm: () => void;
  onCancel: () => void;
  isCreating: boolean;
}) {
  return (
    <div className="create-confirm-overlay" data-testid="create-confirm">
      <div className="create-confirm-dialog">
        <h3 className="confirm-title">Create New Channel</h3>
        <p className="confirm-message">
          Create a new server_readable chat channel? Messages will be encrypted at rest by the server.
        </p>
        <div className="confirm-actions">
          <button
            onClick={onConfirm}
            className="confirm-button"
            disabled={isCreating}
          >
            {isCreating ? 'Creating...' : 'Create'}
          </button>
          <button
            onClick={onCancel}
            className="cancel-button"
            disabled={isCreating}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Message item component.
 */
function MessageItem({
  content,
  createdAt,
  isOwn,
}: {
  content: string;
  createdAt: Date;
  isOwn: boolean;
}) {
  return (
    <div className={`message-item ${isOwn ? 'message-own' : 'message-other'}`} data-testid="message-item">
      <div className="message-bubble">
        <p className="message-content">{content}</p>
        <span className="message-time">{formatDate(createdAt)}</span>
      </div>
    </div>
  );
}

/**
 * Message list component.
 */
function MessageList({
  messages,
}: {
  messages: readonly { id: string; content: string; createdAt: Date; senderAccountId: string }[];
}) {
  if (messages.length === 0) {
    return (
      <div className="messages-empty" data-testid="messages-empty">
        <p>No messages yet. Send the first one!</p>
      </div>
    );
  }

  return (
    <div className="messages-list" data-testid="messages-list">
      {messages.map(msg => (
        <MessageItem
          key={msg.id}
          content={msg.content}
          createdAt={msg.createdAt}
          isOwn={msg.senderAccountId === 'local'}
        />
      ))}
    </div>
  );
}

/**
 * Message input component.
 */
function MessageInput({
  value,
  onChange,
  onSend,
  disabled,
}: {
  value: string;
  onChange: (text: string) => void;
  onSend: () => void;
  disabled: boolean;
}) {
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  };

  return (
    <div className="message-input-container" data-testid="message-input">
      <textarea
        className="message-input"
        value={value}
        onChange={e => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Type a message..."
        rows={1}
        disabled={disabled}
        data-testid="message-textarea"
      />
      <button
        className="send-button"
        onClick={onSend}
        disabled={disabled || value.trim().length === 0}
        data-testid="send-button"
      >
        Send
      </button>
    </div>
  );
}

/**
 * Main view component - routes to appropriate sub-component based on status.
 * 
 * This is the ONLY React component in the feature.
 * All logic is in the program hook; this just renders.
 */
export function ChatView(props: ChatViewProps) {
  const {
    model,
    onStart,
    onOpenChannel,
    onBackToList,
    onShowCreateForm,
    onHideCreateForm,
    onCreateChannel,
    onSendMessage,
    onNewMessageChanged,
    onRetry,
  } = props;

  // Loading state
  if (model.status === 'loading' && model.viewMode === 'channel-list') {
    return (
      <div className="page">
        <ChatHeader counterpart={{ profileId: "", displayName: "Chat" }} />
        <LoadingState />
      </div>
    );
  }

  // Error state
  if (model.status === 'error') {
    return (
      <div className="page">
        <ChatHeader counterpart={{ profileId: "", displayName: "Chat" }} />
        <ErrorState message={model.error || 'An error occurred'} onRetry={onRetry} />
      </div>
    );
  }

  // Create channel confirmation overlay
  if (model.showCreateForm) {
    return (
      <div className="page">
        <ChatHeader counterpart={{ profileId: "", displayName: "Chat" }} onBack={onBackToList} showBack={model.selectedChannelId !== null} />
        <ChannelList
          channels={model.channels}
          onOpenChannel={onOpenChannel}
          onCreate={onShowCreateForm}
        />
        <CreateChannelConfirm
          onConfirm={onCreateChannel}
          onCancel={onHideCreateForm}
          isCreating={model.isCreating}
        />
      </div>
    );
  }

  // Channel detail view
  if (model.viewMode === 'channel-detail' && model.selectedChannelId) {
    // CORRECTION01 (R2 fix): the populated channel (with counterpart)
    // is read from the model. While the channel is loading
    // (selectedChannel === null), the ChatHeader falls back to the
    // "Conversation" placeholder (displayName).
    const selectedChannel = model.selectedChannel;
    const headerCounterpart = selectedChannel?.counterpart
      ? {
          profileId: selectedChannel.counterpart.profileId,
          displayName: selectedChannel.counterpart.displayName,
          age: selectedChannel.counterpart.age,
          cityOrRegion: selectedChannel.counterpart.cityOrRegion,
          photoUrl: selectedChannel.counterpart.photoUrl,
        }
      : { profileId: '', displayName: '' };
    return (
      <div className="page">
        <ChatHeader
          counterpart={headerCounterpart}
          onBack={onBackToList}
          showBack={true}
          realtimeStatus={model.realtimeStatus}
        />
        {model.status === 'loading' ? (
          <LoadingState />
        ) : (
          <>
            <MessageList messages={model.messages} />
            <MessageInput
              value={model.newMessageText}
              onChange={onNewMessageChanged}
              onSend={onSendMessage}
              disabled={model.status !== 'loaded'}
            />
          </>
        )}
      </div>
    );
  }

  // Channel list view (default)
  return (
    <div className="page">
      <ChatHeader counterpart={{ profileId: "", displayName: "Chat" }} />
      <div className="chat-content" data-testid="chat-content">
        <div className="channel-list-header">
          <button
            onClick={onShowCreateForm}
            className="new-channel-button"
            data-testid="new-channel-button"
          >
            + New Channel
          </button>
        </div>
        <ChannelList
          channels={model.channels}
          onOpenChannel={onOpenChannel}
          onCreate={onShowCreateForm}
        />
      </div>
    </div>
  );
}